import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkAuth,
  selectIsAuthenticated,
  selectAuthInitialized,
  selectUser
} from './AuthStore';
import LoadingScreen from '../common/LoadingScreen';

/**
 * AuthProvider bootstraps authentication globally before rendering children.
 * - Checks authentication status on app startup (including SSO session detection)
 * - Shows loading screen on protected routes until auth is determined
 * - Renders children immediately only on login page to avoid UI jank
 * - Handles automatic redirects based on user roles when authentication is successful
 * - For SSO: Calls API even without stored token to detect active sessions
 * - Provides authentication context to the entire app
 */
const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isInitialized = useSelector(selectAuthInitialized);
  const user = useSelector(selectUser);

  // Check authentication on app startup
  useEffect(() => {
    if (!isInitialized) {
      const currentPath = window.location.pathname;

      // Skip checkAuth for public pages that don't require authentication
      const publicPaths = [
        '/doi-mat-khau',
        '/sso-error',
        '/not-found',
        '/not-support-role'
      ];
      if (publicPaths.includes(currentPath)) {
        return;
      }

      dispatch(checkAuth());
    }
  }, [dispatch, isInitialized]);

  // Handle role-based redirects after successful authentication
  useEffect(() => {
    if (isAuthenticated && user && isInitialized) {
      const currentPath = window.location.pathname;

      // Skip redirect if already on login page or password change page
      if (currentPath === '/dang-nhap' || currentPath === '/doi-mat-khau') {
        return;
      }

      // Priority check: if user needs to update password, redirect immediately
      // This handles the case where user just logged in with update_progress === 1
      if (user.update_progress === 1) {
        window.location.href = '/doi-mat-khau';
        return;
      }

      // Role-based redirects only if on home page or root
      if (currentPath === '/' || currentPath === '/home') {
        const roles = user.roles;
        if (roles?.admin) {
          window.location.href = '/not-support-role';
        } else if (roles?.student) {
          window.location.href = '/phong-thi-truc-tuyen';
        } else if (roles?.school) {
          window.location.href = '/khoi';
        } else if (roles?.teacher) {
          window.location.href = '/danh-sach-bai-thi-tu-luan';
        }
      }
    }
  }, [isAuthenticated, user, isInitialized]);

  // Determine whether to show loading or render children
  const shouldShowLoading = useMemo(() => {
    const currentPath = window.location.pathname;

    // Public pages that don't require authentication check - render immediately
    const publicPaths = [
      '/dang-nhap',
      '/doi-mat-khau',
      '/sso-error',
      '/not-found',
      '/not-support-role'
    ];
    if (publicPaths.includes(currentPath)) {
      return false;
    }

    // For protected routes, show loading until auth is initialized
    if (!isInitialized) {
      return true;
    }

    return false;
  }, [isInitialized]);

  // Show loading screen on protected routes until auth is determined
  if (shouldShowLoading) {
    return <LoadingScreen />;
  }

  // Render children once auth state is determined or on login page
  return children;
};

export default AuthProvider;
